import { useState, useMemo, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { config } from "../../utils/config";

import {
  loadRoles,
  setupIntent,
  resumeSubscription,
  subscribe,
  deletePaymentMethod,
  deleteSubscription,
  loadPlans,
  getInvoices,
  updateBillingInfo,
  associateCard,
  getSubscription,
} from "../../service/api_service";

import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

import { Box, Divider, LinearProgress, Skeleton, Stack, Typography, useColorScheme } from "@mui/material";
import { Constants } from "../../utils/constants";
import BorderWidget from "../widgets/border_widget";
import { AccountInfoSkeleton, AccountPlanSkeleton, DefaultButton, ErrorButton, PaymentBillingInfoModal, PaymentCardItem, PaymentDowngradeModal, PaymentFullModal, PaymentHistoryItem, PaymentMethodModal, PaymentPlansModal, SuccessButton, TextButton } from "..";
import ArrowCircleUpOutlinedIcon from '@mui/icons-material/ArrowCircleUpOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import { monthYearWith2Digit } from "../../utils/stringFormat";
import CsvDownloadButton from "react-json-to-csv";
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';

function Account({ jobStore }) {
  useEffect(() => {
    jobStore.setNavigation("account");
  }, [jobStore]);

  const { mode } = useColorScheme();
  const currentMode = mode || "system";
  const [me, setMe] = useState(null);
  const [plans, setPlans] = useState(null);
  const [roles, setRoles] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cardClientSecret, setCardClientSecret] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [subscription, setSubscription] = useState(null);

  const stripePromise = loadStripe(config.STRIPE_PUBLISHABLE_KEY);

  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [isBillingInfoModalOpen, setIsBillingInfoModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isDowngradeModalOpen, setIsDowngradeModalOpen] = useState(false);
  const [isPaymentFullModalOpen, setIsPaymentFullModalOpen] = useState(false);
  const [paymentFullModalErrorMessage, setPaymentFullModalErrorMessage] = useState("");
  const [paymentSetupLoading, setPaymentSetupLoading] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      await refresh();
      const intent = await setupIntent();
      setCardClientSecret(intent.client_secret);
      setLoading(false);
    };
    fetch();
  }, []);

  const refresh = async () => {
    const plans_ = loadPlans();
    const roles_ = loadRoles(true);
    const plans = await plans_;
    const { me, roles } = await roles_;
    setMe(me);
    setPlans(plans);
    setRoles(roles ?? "FREE");
    const invoices = await getInvoices();
    setInvoices(invoices);
    const subscription = await getSubscription();
    setSubscription(subscription);
  };

  const usagePercent = useMemo(() => {
    if (me && me.meter && plans) {
      let percent = (me.meter.predict_urls / plans.find((plan) => plan.plan == roles)?.redirects) * 100;
      percent = Math.min(100, percent.toFixed(2));
      return percent;
    } else {
      return 0;
    }
  }, [me]);

  const getNextRenewalDate = () => {
    if (!subscription?.cancel_at_period_end && subscription?.items?.data?.length > 0) {
      const nextRenewalDate = subscription?.items?.data[subscription?.items?.data?.length - 1]?.current_period_end * 1000;
      return (new Date(nextRenewalDate)).toLocaleDateString();
    } else {
      return "Canceled at " + (new Date(subscription?.canceled_at * 1000)).toLocaleDateString();
    }
  }

  const showDowngradetoFree = async () => {
    setLoading(true);
    setIsDowngradeModalOpen(false);
    await deleteSubscription();
    await refresh();
    setLoading(false);
  };

  const resumeSubscription_ = async () => {
    setLoading(true);
    await resumeSubscription(me?.subscription);
    await refresh();
    setLoading(false);
  };

  const updateSubscription = async (plan) => {
    setIsPlanModalOpen(false);
    console.log(plan);
    setRoles(plan);
    if (plan == "FREE") {
      setIsDowngradeModalOpen(true);
    } else {
      if (me?.payment_method_id && me?.billing_info?.address) {
        setLoading(true);
        await subscribe(plan, "monthly");
        await refresh();
        setLoading(false);
      } else {
        setIsPaymentFullModalOpen(true);
      }
    }
  };

  const onUpdateBillingInfo = async (billingAddress) => {
    setIsBillingInfoModalOpen(false);
    setLoading(true);
    await updateBillingInfo(billingAddress);
    await refresh();
    setLoading(false);
  };

  const onUpdatePaymentMethod = async (paymentMethod) => {
    console.log(paymentMethod);
    setIsPaymentModalOpen(false);
    setLoading(true);
    await associateCard(paymentMethod);
    await subscribe(roles, "monthly");
    await refresh();
    setLoading(false);
  };

  const onUpdatePaymentFullMethod = async (paymentFull) => {
    console.log(paymentFull);
    try {
      setPaymentSetupLoading(true);
      await updateBillingInfo(paymentFull.billingAddress);
      await associateCard(paymentFull.paymentMethod);
      await subscribe(roles, "monthly");
      await refresh();
      setIsPaymentFullModalOpen(false);
      setPaymentSetupLoading(false);
    } catch (error) {
      setPaymentFullModalErrorMessage(error.message);
      setPaymentSetupLoading(false);
    }
  };

  return (
    <Elements stripe={stripePromise}>
      <Box>
        {plans && <PaymentFullModal
          open={isPaymentFullModalOpen}
          onClose={() => setIsPaymentFullModalOpen(false)}
          onBack={() => {
            setIsPaymentFullModalOpen(false);
            setIsPlanModalOpen(true);
          }}
          onFinish={onUpdatePaymentFullMethod}
          currentPlan={plans.find((plan) => plan?.plan?.toLowerCase() === roles?.toLowerCase()) || plans[0]}
          cardClientSecret={cardClientSecret}
          billing_details={me?.billing_info}
          plans={plans}
          errorMessage={paymentFullModalErrorMessage}
          loading={paymentSetupLoading}
        />}
        <PaymentPlansModal
          open={isPlanModalOpen}
          onClose={() => setIsPlanModalOpen(false)}
          onFinish={updateSubscription}
          currentPlan={roles}
          plans={plans}
        />
        <PaymentBillingInfoModal
          open={isBillingInfoModalOpen}
          onClose={() => setIsBillingInfoModalOpen(false)}
          onFinish={onUpdateBillingInfo}
          address={me?.billing_info}
        />
        {plans && <PaymentMethodModal
          open={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          onFinish={onUpdatePaymentMethod}
          currentPlan={plans.find((plan) => plan?.plan?.toLowerCase() === me?.subscription?.toLowerCase()) || plans[0]}
          cardClientSecret={cardClientSecret}
          billing_details={me?.billing_info}
        />}
        <PaymentDowngradeModal
          open={isDowngradeModalOpen}
          onClose={() => setIsDowngradeModalOpen(false)}
          onFinish={showDowngradetoFree}
        />
        <Box>
          <Typography fontWeight={Constants.Styles.fontWeight.semiBold} fontSize={Constants.Styles.fontSize["2xl"]}>
            Billing & Plan
          </Typography>
          <Typography fontSize={Constants.Styles.fontSize.sm} fontWeight={400} color={Constants.Colors.light.mutedForeground}>
            Manage your Redirect Mapper subscription
          </Typography>
        </Box>
        <Stack
          direction={"column"}
          spacing={"1.5rem"}
          sx={{
            mt: "1.125rem",
          }}
        >
          <Stack
            sx={{
              my: "1.5rem",
            }}
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={"1.5rem"}
          >
            {loading ? <AccountPlanSkeleton /> : <BorderWidget
              sx={{
                boxShadow: "0px 1px 3px 0px #0000001a",
                padding: {
                  xs: `calc(${Constants.Styles.spacing} * 2)`,
                  sm: `calc(${Constants.Styles.spacing} * 6)`,
                },
                borderRadius: `calc(${Constants.Styles.radius.base} + 4px)`,
                flex: 1,
              }}
            >
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Typography
                  sx={{
                    fontWeight: Constants.Styles.fontWeight.medium,
                    fontSize: Constants.Styles.fontSize.lg,
                    lineHeight: Constants.Styles.lineHeight.lg,
                  }}
                >Current Plan</Typography>
                <Box
                  sx={{
                    color: Constants.Colors[currentMode].background,
                    backgroundColor: Constants.Colors[currentMode].foreground,
                    borderRadius: `calc(${Constants.Styles.radius.base} - 2px)`,
                    padding: `calc(${Constants.Styles.spacing} / 2) calc(${Constants.Styles.spacing} * 2)`,
                    fontSize: Constants.Styles.fontSize.xs,
                    lineHeight: Constants.Styles.lineHeight.xs,
                  }}
                >{me?.subscription || "FREE"}</Box>
              </Stack>
              <Typography
                sx={{
                  fontWeight: Constants.Styles.fontWeight.normal,
                  fontSize: Constants.Styles.fontSize.sm,
                  lineHeight: Constants.Styles.lineHeight.sm,
                  color: Constants.Colors[currentMode].mutedForeground,
                  mt: "0.5rem",
                  mb: "1.125rem",
                }}
              >{`Up to ${me?.plan_limits || 100} redirects per month`}</Typography>
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Typography
                  sx={{
                    fontWeight: Constants.Styles.fontWeight.normal,
                    fontSize: Constants.Styles.fontSize.sm,
                    lineHeight: Constants.Styles.lineHeight.sm,
                    color: Constants.Colors[currentMode].mutedForeground,
                  }}
                >Monthly Usage</Typography>
                <Typography
                  sx={{
                    fontWeight: Constants.Styles.fontWeight.normal,
                    fontSize: Constants.Styles.fontSize.sm,
                    lineHeight: Constants.Styles.lineHeight.sm,
                    color: Constants.Colors[currentMode].foreground,
                    fontFamily: "Geist",
                  }}
                >{me?.meter?.predict_urls || 0} / {me?.plan_limits || 100}</Typography>
              </Stack>
              <LinearProgress value={usagePercent} variant="determinate"
                sx={{
                  height: 10,
                  borderRadius: 5,
                  color: Constants.Colors[currentMode].primary,
                  backgroundColor: Constants.Colors[currentMode].secondary,
                  marginBlock: "0.5rem",
                }}
              />
              <Typography
                sx={{
                  fontWeight: Constants.Styles.fontWeight.normal,
                  fontSize: Constants.Styles.fontSize.xs,
                  lineHeight: Constants.Styles.lineHeight.xs,
                  color: Constants.Colors[currentMode].mutedForeground,
                  mb: "1.5rem",
                }}
              >{`${usagePercent}% of your monthly limit used`}</Typography>
              <Divider />
              { subscription && subscription.status === "active" && <Stack
                sx={{
                  marginBlock: "0.75rem",
                }}
                spacing={"0.75rem"}
              >
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  <Typography
                    sx={{
                      fontWeight: Constants.Styles.fontWeight.normal,
                      fontSize: Constants.Styles.fontSize.sm,
                      lineHeight: Constants.Styles.lineHeight.sm,
                      color: Constants.Colors[currentMode].mutedForeground,
                    }}
                  >Billing Cycle</Typography>
                  <Typography
                    sx={{
                      fontWeight: Constants.Styles.fontWeight.normal,
                      fontSize: Constants.Styles.fontSize.sm,
                      lineHeight: Constants.Styles.lineHeight.sm,
                      color: Constants.Colors[currentMode].foreground,
                    }}
                  >{me?.frequency}</Typography>
                </Stack>
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  <Typography
                    sx={{
                      fontWeight: Constants.Styles.fontWeight.normal,
                      fontSize: Constants.Styles.fontSize.sm,
                      lineHeight: Constants.Styles.lineHeight.sm,
                      color: Constants.Colors[currentMode].mutedForeground,
                    }}
                  >Next Renewal</Typography>
                  <Typography
                    sx={{
                      fontWeight: Constants.Styles.fontWeight.normal,
                      fontSize: Constants.Styles.fontSize.sm,
                      lineHeight: Constants.Styles.lineHeight.sm,
                      color: Constants.Colors[currentMode].foreground,
                    }}
                  >{getNextRenewalDate()}</Typography>
                </Stack>
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  <Typography
                    sx={{
                      fontWeight: Constants.Styles.fontWeight.normal,
                      fontSize: Constants.Styles.fontSize.sm,
                      lineHeight: Constants.Styles.lineHeight.sm,
                      color: Constants.Colors[currentMode].mutedForeground,
                    }}
                  >Amount</Typography>
                  <Typography
                    sx={{
                      fontWeight: Constants.Styles.fontWeight.normal,
                      fontSize: Constants.Styles.fontSize.sm,
                      lineHeight: Constants.Styles.lineHeight.sm,
                      color: Constants.Colors[currentMode].foreground,
                    }}
                  >{`$${(subscription?.plan?.amount / 100).toFixed(2)}`}</Typography>
                </Stack>
              </Stack> }
              { subscription?.cancel_at_period_end ? <SuccessButton
                text={"Resume Plan"}
                size="medium"
                onClick={() => {
                  resumeSubscription_();
                }}
              /> : <SuccessButton
                text={"Upgrade Plan"}
                size="medium"
                preIcon={<ArrowCircleUpOutlinedIcon />}
                onClick={() => {
                  setIsPlanModalOpen(true);
                }}
              /> }
              {/* {(!me?.billing_info?.address || !me?.payment_method_id) &&
                <Typography
                  sx={{
                    fontWeight: Constants.Styles.fontWeight.normal,
                    fontSize: Constants.Styles.fontSize.xs,
                    lineHeight: Constants.Styles.lineHeight.xs,
                    color: Constants.Colors[currentMode].destructive,
                    mt: "0.5rem",
                  }}
                >
                  You need to set your billing information
                </Typography>
              } */}
            </BorderWidget>}

            {loading ? <AccountInfoSkeleton /> : <BorderWidget
              sx={{
                boxShadow: "0px 1px 3px 0px #0000001a",
                padding: {
                  xs: `calc(${Constants.Styles.spacing} * 2)`,
                  sm: `calc(${Constants.Styles.spacing} * 6)`,
                },
                borderRadius: `calc(${Constants.Styles.radius.base} + 4px)`,
                flex: 1,
              }}
            >
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Typography
                  sx={{
                    fontWeight: Constants.Styles.fontWeight.medium,
                    fontSize: Constants.Styles.fontSize.lg,
                    lineHeight: Constants.Styles.lineHeight.lg,
                  }}
                >Billing Information</Typography>
              </Stack>
              <Typography
                sx={{
                  fontWeight: Constants.Styles.fontWeight.normal,
                  fontSize: Constants.Styles.fontSize.sm,
                  lineHeight: Constants.Styles.lineHeight.sm,
                  color: Constants.Colors[currentMode].mutedForeground,
                  mt: "0.5rem",
                  mb: "1.125rem",
                }}
              >Your payment method and billing details</Typography>
              <Stack
                spacing={"1.0rem"}
                marginBlock={"1.125rem"}
              >
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  <Typography
                    sx={{
                      fontWeight: Constants.Styles.fontWeight.normal,
                      fontSize: Constants.Styles.fontSize.sm,
                      lineHeight: Constants.Styles.lineHeight.sm,
                      color: Constants.Colors[currentMode].mutedForeground,
                    }}
                  >Payment Method</Typography>
                  <TextButton
                    text={"Edit"}
                    size="small"
                    onClick={() => {
                      setIsPaymentModalOpen(true);
                    }}
                    fullWidth={false}
                  />
                </Stack>
                <PaymentCardItem
                  icon={<CreditCardOutlinedIcon />}
                  title={me?.payment_method?.card?.brand ? `Visa ending in ${me?.payment_method?.card?.last4}` : "No payment method"}
                  des={me?.payment_method?.card?.brand ? `Expires ${monthYearWith2Digit(me?.payment_method?.card?.exp_month)}/${monthYearWith2Digit(me?.payment_method?.card?.exp_year)}` : ""}
                />
              </Stack>
              <Divider />
              <Stack
                spacing={"1.0rem"}
                marginBlock={"1.125rem"}
              >
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  <Typography
                    sx={{
                      fontWeight: Constants.Styles.fontWeight.normal,
                      fontSize: Constants.Styles.fontSize.sm,
                      lineHeight: Constants.Styles.lineHeight.sm,
                      color: Constants.Colors[currentMode].mutedForeground,
                    }}
                  >Billing Email</Typography>
                  <TextButton
                    text={"Edit"}
                    size="small"
                    onClick={() => {
                      setIsBillingInfoModalOpen(true);
                    }}
                    fullWidth={false}
                  />
                </Stack>
                <Typography
                  sx={{
                    fontWeight: Constants.Styles.fontWeight.normal,
                    fontSize: Constants.Styles.fontSize.sm,
                    lineHeight: Constants.Styles.lineHeight.sm,
                    color: Constants.Colors[currentMode].foreground,
                  }}
                >
                  {me?.billing_info?.email || "N/A"}
                </Typography>
              </Stack>
              <Divider />
              <Stack
                spacing={"1.0rem"}
                marginBlock={"1.125rem"}
              >
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  <Typography
                    sx={{
                      fontWeight: Constants.Styles.fontWeight.normal,
                      fontSize: Constants.Styles.fontSize.sm,
                      lineHeight: Constants.Styles.lineHeight.sm,
                      color: Constants.Colors[currentMode].mutedForeground,
                    }}
                  >Billing Address</Typography>
                  <TextButton
                    text={"Edit"}
                    size="small"
                    onClick={() => {
                      setIsBillingInfoModalOpen(true);
                    }}
                    fullWidth={false}
                  />
                </Stack>
                {me?.billing_info?.address ? <Box>
                  <Typography
                    sx={{
                      fontWeight: Constants.Styles.fontWeight.normal,
                      fontSize: Constants.Styles.fontSize.sm,
                      lineHeight: Constants.Styles.lineHeight.sm,
                      color: Constants.Colors[currentMode].foreground,
                    }}
                  >
                    {me?.billing_info?.address?.line1}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: Constants.Styles.fontWeight.normal,
                      fontSize: Constants.Styles.fontSize.sm,
                      lineHeight: Constants.Styles.lineHeight.sm,
                      color: Constants.Colors[currentMode].foreground,
                    }}
                  >
                    {`${me?.billing_info?.address?.city}, ${me?.billing_info?.address?.state} ${me?.billing_info?.address?.postal_code}`}
                  </Typography>
                </Box> : <Typography
                  sx={{
                    fontWeight: Constants.Styles.fontWeight.normal,
                    fontSize: Constants.Styles.fontSize.sm,
                    lineHeight: Constants.Styles.lineHeight.sm,
                    color: Constants.Colors[currentMode].foreground,
                  }}
                >
                  Not Set
                </Typography>}
              </Stack>
            </BorderWidget>}
          </Stack>
          <BorderWidget
            sx={{
              boxShadow: "0px 1px 3px 0px #0000001a",
              padding: {
                xs: `calc(${Constants.Styles.spacing} * 2)`,
                sm: `calc(${Constants.Styles.spacing} * 6)`,
              },
              borderRadius: `calc(${Constants.Styles.radius.base} + 4px)`,
              flex: 1,
            }}
          >
            <Stack
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Typography
                sx={{
                  fontWeight: Constants.Styles.fontWeight.medium,
                  fontSize: Constants.Styles.fontSize.lg,
                  lineHeight: Constants.Styles.lineHeight.lg,
                }}
              >
                Payment History
              </Typography>
              {!loading && <CsvDownloadButton
                data={invoices?.data}
                filename={'payment-history.csv'}
                style={{
                  background: "transparent",
                  border: "none",
                }}
              >
                <DefaultButton
                  text={"Download All"}
                  size="small"
                  preIcon={<RequestQuoteOutlinedIcon />}
                  fullWidth={true}
                />
              </CsvDownloadButton>}
            </Stack>
            <Typography
              sx={{
                fontWeight: Constants.Styles.fontWeight.normal,
                fontSize: Constants.Styles.fontSize.sm,
                lineHeight: Constants.Styles.lineHeight.sm,
                color: Constants.Colors[currentMode].mutedForeground,
                mt: "0.125rem",
                mb: "1.125rem",
              }}
            >{`View your past invoices and payments`}</Typography>
            {loading ? <Stack
              spacing={"0.75rem"}
            >
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton
                  key={`invoice_history_index_${index}`}
                  variant="rounded"
                  height={60}
                  width={"100%"}
                  sx={{
                    marginBlock: "1.125rem",
                  }}
                />
              ))}
            </Stack> : invoices?.data?.length > 0 ? <Stack spacing={"0.5rem"}>
              {invoices.data.map((invoice) => (
                <PaymentHistoryItem
                  key={invoice.id}
                  invoice={invoice}
                />
              ))}
            </Stack> : <Typography
              sx={{
                fontWeight: Constants.Styles.fontWeight.normal,
                fontSize: Constants.Styles.fontSize.sm,
                lineHeight: Constants.Styles.lineHeight.sm,
                color: Constants.Colors[currentMode].mutedForeground,
                mt: "0.125rem",
                mb: "1.125rem",
              }}
            >
              No invoices found
            </Typography>}
          </BorderWidget>


        </Stack>
        {me?.subscription &&
          me.subscription !== "FREE" &&
          !me.subscription_canceled &&
          !loading && (
            <BorderWidget
              sx={{
                boxShadow: "0px 1px 3px 0px #0000001a",
                padding: {
                  xs: `calc(${Constants.Styles.spacing} * 2)`,
                  sm: `calc(${Constants.Styles.spacing} * 6)`,
                },
                borderRadius: `calc(${Constants.Styles.radius.base} + 4px)`,
                flex: 1,
                mt: "1.5rem",
              }}
            >
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Typography
                  sx={{
                    fontWeight: Constants.Styles.fontWeight.medium,
                    fontSize: Constants.Styles.fontSize.lg,
                    lineHeight: Constants.Styles.lineHeight.lg,
                  }}
                >Danger Zone</Typography>
              </Stack>
              <Typography
                sx={{
                  fontWeight: Constants.Styles.fontWeight.normal,
                  fontSize: Constants.Styles.fontSize.sm,
                  lineHeight: Constants.Styles.lineHeight.sm,
                  color: Constants.Colors[currentMode].mutedForeground,
                  mt: "0.5rem",
                  mb: "1.125rem",
                }}
              >Downgrade or cancel your subscription</Typography>
              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                spacing={"1.125rem"}
                alignItems={{
                  xs: "start",
                  sm: "flex-end",
                }}
                justifyContent={"space-between"}
              >
                <Box>
                  <Typography
                    sx={{
                      fontWeight: Constants.Styles.fontWeight.normal,
                      fontSize: Constants.Styles.fontSize.base,
                      lineHeight: Constants.Styles.lineHeight.base,
                    }}
                  >
                    Downgrade to Free Plan
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: Constants.Styles.fontWeight.normal,
                      fontSize: Constants.Styles.fontSize.xs,
                      lineHeight: Constants.Styles.lineHeight.xs,
                      color: Constants.Colors[currentMode].mutedForeground,
                      mt: "0.125rem",
                    }}
                  >
                    Limited to 100 redirects per month with basic features
                  </Typography>
                </Box>
                <TextButton
                  text={"Downgrade"}
                  onClick={() => {
                    setIsDowngradeModalOpen(true);
                  }}
                  sx={{
                    maxWidth: 90,
                    color: Constants.Colors[currentMode].destructive,
                    "&:hover": {
                      color: Constants.Colors[currentMode].accentForeground,
                      backgroundColor: Constants.Colors[currentMode].destructive,
                    }
                  }}
                />
              </Stack>
              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                spacing={"1.125rem"}
                alignItems={{
                  xs: "start",
                  sm: "flex-end",
                }}
                justifyContent={"space-between"}
                mt={"0.5rem"}
              >
                <Box>
                  <Typography
                    sx={{
                      fontWeight: Constants.Styles.fontWeight.normal,
                      fontSize: Constants.Styles.fontSize.base,
                      lineHeight: Constants.Styles.lineHeight.base,
                    }}
                  >
                    Delete Account
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: Constants.Styles.fontWeight.normal,
                      fontSize: Constants.Styles.fontSize.xs,
                      lineHeight: Constants.Styles.lineHeight.xs,
                      color: Constants.Colors[currentMode].mutedForeground,
                      mt: "0.125rem",
                    }}
                  >
                    Permanently delete your account and all data
                  </Typography>
                </Box>
                <ErrorButton
                  text={"Delete Account"}
                  onClick={() => {
                    // setIsDowngradeModalOpen(true);
                  }}
                  size="medium"
                  sx={{
                    maxWidth: 190,
                    color: Constants.Colors[currentMode].destructive,
                    "&:hover": {
                      color: Constants.Colors[currentMode].accentForeground,
                      backgroundColor: Constants.Colors[currentMode].destructive,
                    }
                  }}
                />
              </Stack>
            </BorderWidget>
          )}
      </Box>
    </Elements>
  );
}

export default observer(Account);
